/**
 * NotificationBell — bell icon in Navbar showing in-app notifications for
 * titles the user opted into via "Notify Me" on the Coming Soon rail.
 *
 * Lightweight: polls /notifications/me every 90s when signed in. No push,
 * no email — pure in-app feed backed by Mongo.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, Check, X } from "lucide-react";
import { useAuth } from "../lib/AuthContext";
import { api } from "../lib/api";

const POLL_MS = 90 * 1000;

export default function NotificationBell({ compact = false }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const refresh = async () => {
    if (!user) return;
    try {
      const r = await api.get("/notifications/me");
      setItems(r.data?.results || []);
      setUnread(r.data?.unread_count || 0);
    } catch {
      // silent — auth or network blip, retry next interval
    }
  };

  useEffect(() => {
    if (!user) {
      setItems([]); setUnread(0); return;
    }
    refresh();
    const id = setInterval(refresh, POLL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Outside-click closes dropdown
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const handleOpen = async () => {
    setOpen((v) => !v);
    if (!open && unread > 0) {
      // Mark all as read on open (Twitter/Facebook UX). Server is source of truth.
      try {
        await api.post("/notifications/me/read-all");
        setUnread(0);
        setItems((arr) => arr.map((it) => ({ ...it, read: true })));
      } catch {
        // swallow
      }
    }
  };

  const dismiss = async (id, e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    try {
      await api.delete(`/notifications/me/${id}`);
      setItems((arr) => arr.filter((it) => it.id !== id));
    } catch {
      // swallow
    }
  };

  if (!user) return null;

  return (
    <div ref={wrapRef} className={`relative ${compact ? "" : ""}`} data-testid="notif-bell-wrap">
      <button
        type="button"
        onClick={handleOpen}
        aria-label={`Notifications${unread ? ` — ${unread} unread` : ""}`}
        data-testid="notif-bell-button"
        className="relative p-2 rounded-full text-neutral-300 hover:text-white hover:bg-white/5 transition"
      >
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span
            data-testid="notif-bell-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#E50914] text-white text-[10px] font-bold leading-[18px] text-center border-2 border-[#0A0A0A]"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notif-bell-dropdown"
          className="absolute right-0 mt-2 w-80 max-h-[440px] overflow-y-auto bg-[#101012] border border-white/10 rounded-xl shadow-2xl shadow-black/60 z-50 fm-fade"
        >
          <div className="sticky top-0 bg-[#101012] border-b border-white/10 px-4 py-3 flex items-center justify-between">
            <h3 className="text-sm font-bold text-white tracking-wide">Notifications</h3>
            <span className="text-[10px] uppercase tracking-wider text-neutral-500">{items.length} total</span>
          </div>
          {items.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-neutral-500" data-testid="notif-bell-empty">
              <Bell className="w-8 h-8 mx-auto mb-3 text-neutral-700" />
              No notifications yet.<br/>
              <span className="text-xs text-neutral-600">Tap "Notify Me" on upcoming releases.</span>
            </div>
          ) : (
            <ul className="divide-y divide-white/5" data-testid="notif-bell-list">
              {items.map((it) => (
                <li key={it.id} className="group">
                  <Link
                    to={it.link || `/title/${it.media_type}/${it.tmdb_id}`}
                    onClick={() => setOpen(false)}
                    data-testid={`notif-bell-item-${it.id}`}
                    className="flex items-start gap-3 px-4 py-3 hover:bg-white/[0.03] transition"
                  >
                    <div className="mt-1 w-2 h-2 rounded-full flex-shrink-0 bg-emerald-400" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white font-medium leading-snug truncate">{it.title}</p>
                      <p className="text-xs text-neutral-400 leading-snug mt-0.5">{it.message}</p>
                      <p className="text-[10px] uppercase tracking-wider text-neutral-600 mt-1">
                        {new Date(it.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => dismiss(it.id, e)}
                      data-testid={`notif-bell-dismiss-${it.id}`}
                      className="opacity-0 group-hover:opacity-100 transition p-1 -m-1 text-neutral-500 hover:text-white"
                      aria-label="Dismiss"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
